import Markdown, { type MarkdownToJSX } from 'markdown-to-jsx';
import { useState, useEffect, type ReactElement } from 'react';

import { markdownOptions } from '../helpers/markdownOptions';

declare global {
  interface Window {
    PORTAL_COOKIES_SERIAL?: string;
  }
}

interface UseMarkdownFileReturn {
  markdownContent: ReactElement | null;
}

const useMarkdownFile = (file: string): UseMarkdownFileReturn => {
  const [markdownContent, setMarkdownContent] = useState<ReactElement | null>(null);

  useEffect(() => {
    const fetchFileContent = async (): Promise<void> => {
      try {
        const response = await fetch(file);

        if (!response.ok) throw new Error('Bad response');

        let fileContent = await response.text();

        fileContent = fileContent.replace(
          '{{CookieDeclaration}}',
          '<div id="cookiebot-script"></div>'
        );

        const renderedMarkdown = <Markdown options={markdownOptions as MarkdownToJSX.Options}>{fileContent}</Markdown>;

        setMarkdownContent(renderedMarkdown);
      } catch (e) {
        console.error('Error fetching markdown file:', e);
        setMarkdownContent(null);
      }
    };

    fetchFileContent();
  }, [file]);

  useEffect(() => {
    const cookieSerial = window.PORTAL_COOKIES_SERIAL;

    if (!markdownContent || !cookieSerial) return;

    const script = document.createElement('script');
    script.id = 'CookieDeclaration';
    script.src = `https://consent.cookiebot.com/${cookieSerial}/cd.js`;
    script.async = true;

    const placeholder = document.getElementById('cookiebot-script');
    if (placeholder) {
      placeholder.appendChild(script);
    }

    return () => {
      if (placeholder) placeholder.removeChild(script);
    };
  }, [markdownContent]);

  return { markdownContent };
};

export default useMarkdownFile;
